import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Group as GroupIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'episcop' | 'vicar' | 'paroh' | 'secretar' | 'contabil';
  parohieId?: string;
  parohieName?: string;
  permissions: string[];
  isActive: boolean;
  lastLogin?: string;
  createdAt: string;
}

interface Parohie {
  id: string;
  name: string;
  address: string;
  paroh: string;
}

const AdministrareUtilizatori: React.FC = () => {
  const { hasPermission, user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Mock data pentru utilizatori
  const [users] = useState<User[]>([
    {
      id: '1',
      email: 'episcop@episcopie.ro',
      name: 'Episcop',
      role: 'episcop',
      permissions: ['all'],
      isActive: true,
      lastLogin: '2024-01-20 10:30',
      createdAt: '2023-01-01'
    },
    {
      id: '2',
      email: 'vicar@episcopie.ro',
      name: 'Vicar',
      role: 'vicar',
      permissions: ['registratura.read', 'registratura.write', 'gestiune.read', 'concedii.read', 'concedii.write', 'utilizatori.read'],
      isActive: true,
      lastLogin: '2024-01-20 09:15',
      createdAt: '2023-01-15'
    },
    {
      id: '3',
      email: 'paroh@parohia1.ro',
      name: 'Paroh 1',
      role: 'paroh',
      parohieId: 'parohia1',
      parohieName: 'Parohia Sf. Nicolae',
      permissions: ['registratura.read', 'gestiune.read', 'gestiune.write', 'concedii.read', 'concedii.write'],
      isActive: true,
      lastLogin: '2024-01-19 16:45',
      createdAt: '2023-02-01'
    },
    {
      id: '4',
      email: 'secretar@episcopie.ro',
      name: 'Secretar',
      role: 'secretar',
      permissions: ['registratura.read', 'registratura.write', 'concedii.read'],
      isActive: true,
      lastLogin: '2024-01-20 08:20',
      createdAt: '2023-03-01'
    },
    {
      id: '5',
      email: 'contabil@episcopie.ro',
      name: 'Contabil',
      role: 'contabil',
      permissions: ['gestiune.read', 'gestiune.write'],
      isActive: false,
      lastLogin: '2024-01-15 14:30',
      createdAt: '2023-03-15'
    }
  ]);

  // Mock data pentru parohii
  const [parohii] = useState<Parohie[]>([
    { id: 'parohia1', name: 'Parohia 1', address: 'Str. 1', paroh: 'Părintele 1' },
    { id: 'parohia2', name: 'Parohia 2', address: 'Str. 5', paroh: 'Părintele 2' },
    { id: 'parohia3', name: 'Parohia 3', address: 'Str. 10', paroh: 'Părintele 3' }
  ]);

  const roles = [
    { value: 'episcop', label: 'Episcop', description: 'Toate permisiunile' },
    { value: 'vicar', label: 'Vicar', description: 'Permisiuni administrative' },
    { value: 'paroh', label: 'Paroh', description: 'Administrare parohie' },
    { value: 'secretar', label: 'Secretar', description: 'Registratura și concedii' },
    { value: 'contabil', label: 'Contabil', description: 'Gestiune financiară' }
  ];

  const allPermissions = [
    { key: 'registratura.read', label: 'Citire registratura' },
    { key: 'registratura.write', label: 'Scriere registratura' },
    { key: 'gestiune.read', label: 'Citire gestiune' },
    { key: 'gestiune.write', label: 'Scriere gestiune' },
    { key: 'concedii.read', label: 'Citire concedii' },
    { key: 'concedii.write', label: 'Scriere concedii' },
    { key: 'utilizatori.read', label: 'Citire utilizatori' },
    { key: 'utilizatori.write', label: 'Scriere utilizatori' },
    { key: 'all', label: 'Toate permisiunile' }
  ];

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.parohieName && user.parohieName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRole = !filterRole || user.role === filterRole;
    const matchesStatus = filterStatus === '' || 
      (filterStatus === 'active' && user.isActive) ||
      (filterStatus === 'inactive' && !user.isActive);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'episcop': return 'error';
      case 'vicar': return 'warning';
      case 'paroh': return 'primary';
      case 'secretar': return 'info';
      case 'contabil': return 'success';
      default: return 'default';
    }
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setIsEditing(false);
    setOpenDialog(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setIsEditing(false);
  };

  const handleToggleUserStatus = (userId: string) => {
    // Implementare toggle status utilizator
    console.log('Toggle status pentru utilizator:', userId);
  };

  const handleDeleteUser = (userId: string) => {
    // Implementare ștergere utilizator
    console.log('Ștergere utilizator:', userId);
  };

  const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.isActive).length;
  const inactiveUsers = users.filter(u => !u.isActive).length;
  const parohiCount = users.filter(u => u.role === 'paroh').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Administrare Utilizatori
        </Typography>
        {hasPermission('utilizatori.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare utilizator nou */}}
          >
            Utilizator nou
          </Button>
        )}
      </Box>

      {/* Statistici */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <GroupIcon color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Total utilizatori
                </Typography>
              </Box>
              <Typography variant="h5">
                {totalUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PersonIcon color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Activi
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                {activeUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LockIcon color="error" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Inactivi
                </Typography>
              </Box>
              <Typography variant="h5" color="error.main">
                {inactiveUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AdminIcon color="info" sx={{ mr: 1 }} />
                <Typography color="textSecondary">
                  Parohi
                </Typography>
              </Box>
              <Typography variant="h5" color="info.main">
                {parohiCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Toți utilizatorii" />
          <Tab label="Activi" />
          <Tab label="Inactivi" />
          <Tab label="Parohii" />
        </Tabs>

        {/* Tab Toți utilizatorii */}
        <TabPanel value={activeTab} index={0}>
          {/* Filtre */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Caută utilizatori..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Rol</InputLabel>
                  <Select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    label="Rol"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    {roles.map(role => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">Toate</MenuItem>
                    <MenuItem value="active">Activi</MenuItem>
                    <MenuItem value="inactive">Inactivi</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterRole('');
                    setFilterStatus('');
                  }}
                >
                  Resetează
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Tabel utilizatori */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nume</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell>Parohie</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ultima autentificare</TableCell>
                  <TableCell>Acțiuni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        {user.name}
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={roles.find(r => r.value === user.role)?.label}
                        color={getRoleColor(user.role) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.parohieName || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Activ' : 'Inactiv'}
                        color={user.isActive ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.lastLogin || 'Niciodată'}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewUser(user)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      {hasPermission('utilizatori.write') && (
                        <>
                          <IconButton
                            onClick={() => handleEditUser(user)}
                            color="secondary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleToggleUserStatus(user.id)}
                            color={user.isActive ? 'error' : 'success'}
                          >
                            {user.isActive ? <LockIcon /> : <UnlockIcon />}
                          </IconButton>
                          {user.id !== currentUser?.id && (
                            <IconButton
                              onClick={() => handleDeleteUser(user.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab-uri pentru statusuri specifice */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Utilizatori activi
          </Typography>
          <List>
            {users.filter(u => u.isActive).map((user) => (
              <React.Fragment key={user.id}>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={user.name}
                    secondary={`${user.email} • ${roles.find(r => r.value === user.role)?.label} • Ultima autentificare: ${user.lastLogin || 'Niciodată'}`}
                  />
                  <Chip
                    label={user.isActive ? 'Activ' : 'Inactiv'}
                    color={user.isActive ? 'success' : 'error'}
                    size="small"
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Utilizatori inactivi
          </Typography>
          <List>
            {users.filter(u => !u.isActive).map((user) => (
              <React.Fragment key={user.id}>
                <ListItem>
                  <ListItemIcon>
                    <LockIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={user.name}
                    secondary={`${user.email} • ${roles.find(r => r.value === user.role)?.label} • Ultima autentificare: ${user.lastLogin || 'Niciodată'}`}
                  />
                  <Chip
                    label={user.isActive ? 'Activ' : 'Inactiv'}
                    color={user.isActive ? 'success' : 'error'}
                    size="small"
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Parohii și parohi
          </Typography>
          <List>
            {parohii.map((parohie) => (
              <React.Fragment key={parohie.id}>
                <ListItem>
                  <ListItemIcon>
                    <AdminIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={parohie.name}
                    secondary={`${parohie.address} • Paroh: ${parohie.paroh}`}
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>
      </Paper>

      {/* Dialog pentru vizualizare/editare utilizator */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? 'Editare utilizator' : 'Detalii utilizator'} - {selectedUser?.name}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nume"
                  value={selectedUser.name}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={selectedUser.email}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditing}>
                  <InputLabel>Rol</InputLabel>
                  <Select
                    value={selectedUser.role}
                    label="Rol"
                  >
                    {roles.map(role => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditing}>
                  <InputLabel>Parohie</InputLabel>
                  <Select
                    value={selectedUser.parohieId || ''}
                    label="Parohie"
                  >
                    <MenuItem value="">-</MenuItem>
                    {parohii.map(parohie => (
                      <MenuItem key={parohie.id} value={parohie.id}>
                        {parohie.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedUser.isActive}
                      disabled={!isEditing}
                    />
                  }
                  label="Utilizator activ"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Permisiuni
                </Typography>
                <FormGroup>
                  {allPermissions.map(permission => (
                    <FormControlLabel
                      key={permission.key}
                      control={
                        <Checkbox
                          checked={selectedUser.permissions.includes(permission.key)}
                          disabled={!isEditing}
                        />
                      }
                      label={permission.label}
                    />
                  ))}
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Creat la
                </Typography>
                <Typography variant="body1">
                  {selectedUser.createdAt}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Ultima autentificare
                </Typography>
                <Typography variant="body1">
                  {selectedUser.lastLogin || 'Niciodată'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {isEditing ? 'Anulează' : 'Închide'}
          </Button>
          {isEditing && (
            <Button variant="contained">
              Salvează modificările
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdministrareUtilizatori;

